import { useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';

export const btnPrimary =
  'px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed';
export const btnGhost =
  'px-3 py-1.5 rounded-lg bg-[#171022] hover:bg-[#221733] border border-[#2c2140] text-xs font-semibold text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed';
export const btnDanger =
  'px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed';
export const inputCls =
  'w-full px-3 py-2 bg-[#171022] border border-[#2c2140] rounded-lg text-xs text-white focus:outline-none focus:border-violet-500';
export const cardCls = 'bg-[#120d1a] border border-[#241b33] rounded-xl';

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export function timeAgo(iso: string): string {
  const secs = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (Number.isNaN(secs)) return '';
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** Asks before doing something that is hard to undo. */
export function ConfirmModal({
  open,
  title,
  description,
  confirmLabel,
  busy,
  error,
  danger,
  onConfirm,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  busy: boolean;
  error: string | null;
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
}) {
  return (
    <Modal open={open} onOpenChange={(o) => !o && !busy && onClose()} title={title} description={description} maxWidth="md">
      <div className="space-y-4">
        {children}
        {error && (
          <p className="text-xs text-rose-400" role="alert">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className={btnGhost}>
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={busy} className={danger ? btnDanger : btnPrimary}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Shows a temporary password once, with a Copy button. */
export function TempPasswordModal({
  password,
  who,
  onClose,
}: {
  password: string | null;
  who: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password ?? '');
      setCopied(true);
      setCopyFailed(false);
    } catch {
      setCopyFailed(true);
    }
  };

  return (
    <Modal
      open={password !== null}
      onOpenChange={(o) => {
        if (!o) {
          setCopied(false);
          setCopyFailed(false);
          onClose();
        }
      }}
      title="Temporary password"
      description={who}
      maxWidth="md"
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <code className="flex-1 px-3 py-2.5 rounded-lg bg-black/40 border border-[#2c2140] text-sm font-mono text-white select-all break-all">
            {password}
          </code>
          <button type="button" onClick={copy} className={`${btnGhost} flex items-center gap-1.5`}>
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        {copyFailed && <p className="text-xs text-amber-400">Could not copy automatically. Select the password and copy it by hand.</p>}
        <p className="text-xs text-gray-300">
          Give this to them; they must choose their own password when they first sign in. It will not be shown again.
        </p>
        <div className="flex justify-end">
          <button type="button" onClick={onClose} className={btnPrimary}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}
